import React, { useState, useRef, useEffect } from 'react';
import { Github, Linkedin, Globe, Palette, Send, FileText, Briefcase, Mail, AlertTriangle, Download, X, File, Heart, MessageCircle, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import './ChatInterface.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { API_BASE_URL } from '../config/api';
import Modal from './Modal';

// --- RESUME DOWNLOAD MODAL ---
const ResumeDownloadModal = ({ isOpen, onClose, theme }) => {
  if (!isOpen) return null;

  const showToast = (message) => {
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 left-1/2 -translate-x-1/2 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg z-[100] animate-fade-in-down text-sm font-medium flex items-center gap-2';
    toast.innerHTML = `<span>${message}</span>`;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  };

  const handleDownload = async (format) => {
    const filename = `resume.${format}`;
    const path = `/assets/resumes/${filename}`;

    try {
      const response = await fetch(path);
      if (response.status === 404) {
        showToast('⚠️ Format not available');
      } else if (response.ok) {
        // Trigger download
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        onClose();
      } else {
        // Any other non-OK status (500, etc.)
        showToast('⚠️ Download failed, please try again');
      }
    } catch (error) {
      console.error('Download error:', error);
      showToast('⚠️ Download failed, please try again');
    }
  };

  return (
    <Modal
      onClose={onClose}
      labelledBy="resume-modal-title"
      describedBy="resume-modal-desc"
      overlayClassName="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/20 backdrop-blur-sm animate-fade-in"
      contentClassName={`relative w-full max-w-xs p-6 rounded-2xl shadow-2xl transform transition-all scale-100 ${theme.chat.card} border border-gray-100`}
    >
      <button
        onClick={onClose}
        aria-label="Close"
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-100 transition-colors opacity-50 hover:opacity-100"
      >
        <X size={16} />
      </button>

      <h3 id="resume-modal-title" className="text-lg font-bold mb-1">Download Resume</h3>
      <p id="resume-modal-desc" className="text-xs opacity-60 mb-6">Select your preferred format</p>

      <div className="space-y-3">
          <button
            onClick={() => handleDownload('pdf')}
            className="w-full flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:border-red-500 hover:bg-red-50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText size={16} />
              </div>
              <span className="font-medium text-sm">PDF Document</span>
            </div>
            <Download size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500" />
          </button>

          <button
            onClick={() => handleDownload('docx')}
            className="w-full flex items-center justify-between p-3 rounded-xl border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform">
                <File size={16} />
              </div>
              <span className="font-medium text-sm">Word Document</span>
            </div>
            <Download size={14} className="opacity-0 group-hover:opacity-100 transition-opacity text-blue-500" />
          </button>
        </div>
    </Modal>
  );
};

const ChatInterface = () => {
  // Use global theme context
  const { currentTheme, cycleTheme, theme } = useTheme();

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Used for network request status
  const [isTyping, setIsTyping] = useState(false); // Used for UI typing indicator
  const [showInitialQuestion, setShowInitialQuestion] = useState(false); // Start false for onboarding
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null); // Synchronous mirror of sessionId to avoid races on rapid sends
  const [showResumeModal, setShowResumeModal] = useState(false); // Resume modal state
  const [logoCompleted, setLogoCompleted] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null); // Ref for chat input focus
  const logoCompletedTimerRef = useRef(null);
  const orbRef = useRef(null); // Orb element — the interaction engine writes CSS vars onto it
  const orbPulseRef = useRef(0); // 0..1 "excitement" pulse fed by chat activity, decays in the engine
  // Drives the paced "typewriter" reveal so replies appear gradually even when
  // the backend returns everything at once.
  const revealRef = useRef({ id: null, full: '', shown: 0, done: false, raf: null, last: 0, lastCut: 0, finalize: null });
  const logoState = isLoading ? 'thinking' : isTyping ? 'typing' : logoCompleted ? 'success' : 'idle';
  const createMessage = (sender, text, type = 'text', extra = {}) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
    sender,
    text,
    type,
    ...extra,
  });

  // --- ONBOARDING SEQUENCE ---
  useEffect(() => {
    const startOnboarding = async () => {
      // 1. Wait 500ms
      await new Promise(r => setTimeout(r, 500));

      // 2. Show Typing Indicator
      setIsTyping(true);

      // 3. Wait 1500ms
      await new Promise(r => setTimeout(r, 1500));

      // 4. Show Initial State
      setIsTyping(false);
      setShowInitialQuestion(true);
    };

    startOnboarding();
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    return () => {
      clearTimeout(logoCompletedTimerRef.current);
      stopReveal();
    };
  }, []);

  const triggerLogoCompleted = () => {
    clearTimeout(logoCompletedTimerRef.current);
    setLogoCompleted(true);
    logoCompletedTimerRef.current = setTimeout(() => {
      setLogoCompleted(false);
    }, 900);
  };

  // --- PACED TEXT REVEAL (typewriter) -------------------------------------
  // Reveals the accumulated reply a few characters per frame so the bubble
  // grows steadily instead of snapping in. `revealRef.full` is the target
  // text (grows as stream tokens arrive); `done` marks the stream finished.
  // Base pace for short replies; longer replies speed up (see runReveal) so a
  // big paragraph never crawls. MAX_REVEAL_SECONDS caps the total reveal time.
  const REVEAL_CHARS_PER_SEC = 45;
  const MAX_REVEAL_SECONDS = 2.6;

  const stopReveal = () => {
    if (revealRef.current.raf) clearInterval(revealRef.current.raf);
    revealRef.current.raf = null;
  };

  const commitReveal = () => {
    const s = revealRef.current;
    setMessages(prev => prev.map(m =>
      m.id === s.id ? { ...m, text: s.full, streaming: false, ...(s.finalize || {}) } : m
    ));
  };

  const runReveal = () => {
    stopReveal();
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      const s = revealRef.current;
      s.shown = s.full.length;
      if (s.done) commitReveal();
      else setMessages(prev => prev.map(m => (m.id === s.id ? { ...m, text: s.full } : m)));
      return;
    }

    revealRef.current.last = 0;
    // A timestamp-driven interval (not requestAnimationFrame): timers keep
    // firing even when the tab is backgrounded, so the reveal still finishes
    // and finalizes instead of freezing an empty bubble. The dt-based advance
    // self-corrects — a throttled tick with a large dt simply reveals more.
    //
    // We advance a fractional character cursor for smooth pacing, but only
    // ever expose whole words (snap back to the last space). Each new word
    // fades in via CSS, so the reply flows in word-by-word instead of
    // clattering out character-by-character.
    const tick = () => {
      const s = revealRef.current;
      const now = performance.now();
      if (!s.last) s.last = now;
      const dt = now - s.last;
      s.last = now;

      if (s.shown < s.full.length) {
        // Speed up proportionally for long replies so the whole thing reveals
        // within ~MAX_REVEAL_SECONDS; short replies keep the calm base pace.
        const rate = Math.max(REVEAL_CHARS_PER_SEC, s.full.length / MAX_REVEAL_SECONDS);
        s.shown = Math.min(s.full.length, s.shown + Math.max(1, (rate * dt) / 1000));
      }

      // Snap the visible cut to a word boundary so partial words never flash.
      let cut = Math.floor(s.shown);
      if (s.shown < s.full.length) {
        const space = s.full.lastIndexOf(' ', cut);
        cut = space > s.lastCut ? space : s.lastCut;
      }

      // Only re-render when a new whole word is ready — keeps it light and lets
      // each word's fade play out cleanly.
      if (cut !== s.lastCut) {
        s.lastCut = cut;
        const text = s.full.slice(0, cut);
        setMessages(prev => prev.map(m => (m.id === s.id ? { ...m, text } : m)));
        // Each word landing gives the orb a small heartbeat tick.
        orbPulseRef.current = Math.min(1, orbPulseRef.current + 0.35);
      }

      if (s.shown >= s.full.length && s.done) {
        commitReveal();
        stopReveal();
      }
    };
    revealRef.current.raf = setInterval(tick, 16);
  };

  // --- ORB INTERACTION ENGINE ---------------------------------------------
  // One rAF loop drives two behaviors via CSS vars on the orb element:
  //  · cursor: the swirl leans toward the pointer and the orb "perks up"
  //    (scale + saturation) as the cursor approaches;
  //  · chat: orbPulseRef spikes on chat activity (words streaming in, user
  //    typing) and decays here, giving a heartbeat-like tick.
  useEffect(() => {
    const orb = orbRef.current;
    if (!orb) return undefined;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return undefined;

    const s = { dx: 0, dy: 0, tdx: 0, tdy: 0, prox: 0, tprox: 0, boost: 0, frame: null, last: 0 };

    const onPointerMove = (e) => {
      const rect = orb.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const vx = e.clientX - cx;
      const vy = e.clientY - cy;
      const dist = Math.hypot(vx, vy);
      // Lean toward the cursor, up to 3px.
      const lean = Math.min(3, dist / 60);
      s.tdx = dist > 0 ? (vx / dist) * lean : 0;
      s.tdy = dist > 0 ? (vy / dist) * lean : 0;
      // Excitement rises within ~200px of the orb.
      s.tprox = Math.max(0, 1 - dist / 200);
    };

    const onPointerLeave = () => {
      s.tdx = 0;
      s.tdy = 0;
      s.tprox = 0;
    };

    const tick = (now) => {
      if (!s.last) s.last = now;
      const dt = Math.min(100, now - s.last);
      s.last = now;

      // Smooth-follow the targets; decay the chat pulse over ~700ms.
      s.dx += (s.tdx - s.dx) * 0.12;
      s.dy += (s.tdy - s.dy) * 0.12;
      s.prox += (s.tprox - s.prox) * 0.1;
      orbPulseRef.current = Math.max(0, orbPulseRef.current - dt / 700);

      const boost = Math.min(1, s.prox * 0.7 + orbPulseRef.current * 0.6);
      if (
        Math.abs(boost - s.boost) > 0.001 ||
        Math.abs(s.dx - s.pdx || 0) > 0.01 ||
        Math.abs(s.dy - s.pdy || 0) > 0.01
      ) {
        orb.style.setProperty('--orb-dx', `${s.dx.toFixed(2)}px`);
        orb.style.setProperty('--orb-dy', `${s.dy.toFixed(2)}px`);
        orb.style.setProperty('--orb-boost', boost.toFixed(3));
        s.boost = boost;
        s.pdx = s.dx;
        s.pdy = s.dy;
      }
      s.frame = requestAnimationFrame(tick);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    window.addEventListener('pointerleave', onPointerLeave);
    window.addEventListener('blur', onPointerLeave);
    s.frame = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerleave', onPointerLeave);
      window.removeEventListener('blur', onPointerLeave);
      cancelAnimationFrame(s.frame);
    };
  }, []);

  // Focus Management: Keep input focused unless user explicitly clicks away
  useEffect(() => {
    if (!isLoading && inputRef.current) {
      // Small timeout to ensure DOM is ready and prevent race conditions
      setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
    }
  }, [messages, isTyping, isLoading]);

  const handleOptionClick = async (option) => {
    setShowInitialQuestion(false);

    const optionMessages = {
      'build with me': 'im looking to build with you',
      'date me maybe': 'im looking for a life partner',
      'let\u2019s talk': 'im looking for convo',
      'back my ideas': 'im looking to invest in you'
    };

    const userText = optionMessages[option];
    const userMessage = createMessage('you', userText);
    setMessages(prev => [...prev, userMessage]);

    // Trigger Rich Media Responses based on intent
    let richMediaType = 'text';
    if (option === 'build with me') richMediaType = 'resume_card';
    if (option === 'back my ideas') richMediaType = 'project_carousel';
    if (option === 'let\u2019s talk') richMediaType = 'contact_card';

    await sendMessage(userText, richMediaType);
  };

  // Legacy non-streaming path — used if the SSE endpoint is unavailable.
  const sendMessageLegacy = async (messageText, forcedType) => {
    const response = await fetch(`${API_BASE_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: messageText, session_id: sessionIdRef.current })
    });
    if (!response.ok) {
      const err = new Error(`HTTP error! status: ${response.status}`);
      err.status = response.status;
      throw err;
    }

    const data = await response.json();
    if (data.session_id) {
      sessionIdRef.current = data.session_id;
      setSessionId(data.session_id);
    }
    setIsTyping(false);
    const backendType = data.response_type || 'text';
    const resolvedType = backendType !== 'text' ? backendType : (forcedType || backendType);
    const botResponse = data.response || "Sorry, couldn't get a response.";
    const m = createMessage('aayush', '', 'text', { streaming: true, model: data.model });
    revealRef.current = {
      id: m.id, full: botResponse, shown: 0, done: true, raf: null, last: 0, lastCut: 0,
      finalize: { type: resolvedType, model: data.model },
    };
    setMessages(prev => [...prev, m]);
    runReveal();
    triggerLogoCompleted();
  };

  // Streaming path: reads SSE frames from /api/chat/stream and grows the bot
  // message in place as tokens arrive.
  const sendMessageStreaming = async (messageText, forcedType) => {
    const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message: messageText, session_id: sessionIdRef.current })
    });
    if (!response.ok || !response.body) {
      // Carry the HTTP status so sendMessage can decide: client errors (4xx)
      // would fail identically on the legacy endpoint, so don't retry those.
      const err = new Error(`stream unavailable (${response.status})`);
      err.status = response.status;
      throw err;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    let streamedMsgId = null;
    let meta = null;

    // Create the (empty) bubble once, then let the reveal loop grow it as
    // `revealRef.full` catches up to the accumulated stream text.
    const ensureMessage = () => {
      if (streamedMsgId) return;
      setIsTyping(false);
      const m = createMessage('aayush', '', 'text', { streaming: true });
      streamedMsgId = m.id;
      revealRef.current = { id: m.id, full: '', shown: 0, done: false, raf: null, last: 0, lastCut: 0, finalize: null };
      setMessages(prev => [...prev, m]);
      runReveal();
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const frames = buffer.split('\n\n');
      buffer = frames.pop(); // keep the trailing partial frame
      for (const frame of frames) {
        const line = frame.trim();
        if (!line.startsWith('data:')) continue;
        let evt;
        try {
          evt = JSON.parse(line.slice(5).trim());
        } catch {
          continue;
        }
        if (evt.type === 'delta' && evt.text) {
          fullText += evt.text;
          ensureMessage();
          revealRef.current.full = fullText;
        } else if (evt.type === 'done') {
          meta = evt;
        }
      }
    }

    if (meta?.session_id) {
      sessionIdRef.current = meta.session_id;
      setSessionId(meta.session_id);
    }
    setIsTyping(false);
    const backendType = meta?.response_type || 'text';
    const resolvedType = backendType !== 'text' ? backendType : (forcedType || 'text');
    const finalText = fullText || meta?.response || "Sorry, couldn't get a response.";

    ensureMessage(); // covers the case where no deltas ever arrived
    revealRef.current.full = finalText;
    revealRef.current.finalize = { type: resolvedType, model: meta?.model };
    revealRef.current.done = true;
    runReveal(); // keep revealing until it catches up, then finalize
    triggerLogoCompleted();
  };

  const sendMessage = async (messageText, forcedType = null) => {
    if (!messageText.trim()) return;

    setIsLoading(true);
    setIsTyping(true);

    try {
      try {
        await sendMessageStreaming(messageText, forcedType);
      } catch (streamError) {
        // 4xx = the request itself was rejected (rate limit, too long…);
        // the legacy endpoint would reject it identically, so don't retry.
        if (streamError.status >= 400 && streamError.status < 500) throw streamError;
        console.warn('Streaming failed, falling back to /api/chat:', streamError);
        await sendMessageLegacy(messageText, forcedType);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setIsTyping(false);
      // Status-aware error cards instead of a generic "offline" for everything.
      let errorText = "My brain is briefly offline, but my inbox is open.";
      if (error.status === 429) {
        errorText = "Whoa, that's a lot of messages at once — give me a few seconds to catch up, then try again.";
      } else if (error.status === 422) {
        errorText = "That message is a bit too long for me. Mind trimming it down and sending again?";
      }
      setMessages(prev => [...prev, createMessage('aayush', errorText, 'error')]);
      triggerLogoCompleted();
    } finally {
      setIsLoading(false);
      // Re-focus input after sending
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  // Auto-grow the textarea up to a max height, then scroll internally.
  const autoResize = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    autoResize(e.target);
    // The orb perks up while the visitor types — it's "listening".
    orbPulseRef.current = Math.min(1, orbPulseRef.current + 0.25);
  };

  const handleInputKeyDown = (e) => {
    // Enter sends, Shift+Enter inserts a newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleSendMessage = async () => {
    if (inputValue.trim() && !isLoading) {
      const messageText = inputValue.trim();
      const userMessage = createMessage('you', messageText);
      setMessages(prev => [...prev, userMessage]);
      setInputValue('');
      setShowInitialQuestion(false);

      // Reset the textarea height and keep focus.
      if (inputRef.current) {
        inputRef.current.style.height = 'auto';
        inputRef.current.focus();
      }

      await sendMessage(messageText);
    }
  };

  // --- RICH MEDIA RENDERER ---
  const renderMessage = (msg) => {
    // 1. Error / Offline Card
    if (msg.type === 'error') {
      return (
        <div className={`p-4 ${theme.chat.card} border-l-4 border-l-amber-500 bg-amber-50/50`}>
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-amber-500 shrink-0" size={20} />
            <div className="space-y-2">
              <p className="text-sm font-medium text-amber-900">{msg.text}</p>
              <a
                href="mailto:aayushpanwar601@gmail.com"
                className="inline-flex items-center gap-2 text-xs font-bold text-amber-700 hover:underline"
              >
                <Mail size={14} />
                Send Email Instead
              </a>
            </div>
          </div>
        </div>
      );
    }

    // 2. Resume Card
    if (msg.type === 'resume_card') {
      return (
        <div className="space-y-3">
          <div className={theme.chat.botBubble + " px-4 py-2.5"}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                strong: ({ node, ...props }) => <span className="font-bold" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-4 my-2 space-y-1" {...props} />,
                li: ({ node, ...props }) => <li className="" {...props} />
              }}
            >
              {msg.text}
            </ReactMarkdown>
          </div>
          <div className={`p-4 ${theme.chat.card} max-w-sm`}>
            <div className="flex items-center gap-4 mb-3">
              <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
                <FileText size={24} className="text-gray-600" />
              </div>
              <div>
                <h3 className="font-bold text-sm">Aayush Panwar</h3>
                <p className="text-xs opacity-60">DevSecOps</p>
              </div>
            </div>
            <button
              onClick={() => setShowResumeModal(true)}
              className="w-full py-2 bg-black text-white rounded-lg text-sm font-medium hover:opacity-80 transition-opacity flex items-center justify-center gap-2"
            >
              <Briefcase size={16} />
              Download Resume
            </button>
          </div>
        </div>
      );
    }

    // 3. Social Links Card
    if (msg.type === 'social_links') {
      return (
        <div className="space-y-3">
          <div className={theme.chat.botBubble + " px-4 py-2.5"}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ node, ...props }) => <p className="mb-2 last:mb-0" {...props} />,
                strong: ({ node, ...props }) => <span className="font-bold" {...props} />,
                ul: ({ node, ...props }) => <ul className="list-disc pl-4 my-2 space-y-1" {...props} />,
                li: ({ node, ...props }) => <li className="" {...props} />,
                a: ({ node, ...props }) => <a className="text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer" {...props} />,
                code: ({ node, inline, ...props }) => inline
                  ? <code className="bg-black/10 px-1 py-0.5 rounded text-xs font-mono" {...props} />
                  : <code className="block bg-black/10 p-2 rounded text-xs font-mono my-2 overflow-x-auto" {...props} />
              }}
            >
              {msg.text}
            </ReactMarkdown>
          </div>
          <div className={`p-4 ${theme.chat.card} grid grid-cols-2 gap-3`}>
            {[
              { Icon: Github, label: "GitHub", href: "https://github.com/aayush-panwar" },
              { Icon: Linkedin, label: "LinkedIn", href: "https://linkedin.com/in/aayush-panwar" }
            ].map(({ Icon, label, href }, idx) => (
              <a
                key={idx}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-300 hover:scale-[1.02] ${theme.button?.option || "bg-white border-black/10"}`}
              >
                <Icon size={18} />
                <span className="text-sm font-medium">{label}</span>
              </a>
            ))}
          </div>
        </div>
      );
    }

    // 4. Project Carousel (simplified - just shows text response)
    if (msg.type === 'project_carousel') {
      return (
        <div className="space-y-3">
          <div className={theme.chat.botBubble + " px-4 py-2.5"}>{msg.text}</div>
        </div>
      );
    }

    // 4. Contact Card
    if (msg.type === 'contact_card') {
      return (
        <div className="space-y-3">
          <div className={theme.chat.botBubble + " px-4 py-2.5"}>{msg.text}</div>
          <div className={`p-4 ${theme.chat.card} grid grid-cols-2 gap-2`}>
            {[
              { label: 'Email', icon: Mail, href: 'mailto:aayushpanwar601@gmail.com' },
              { label: 'GitHub', icon: Github, href: 'https://github.com/aayush-panwar' },
              { label: 'LinkedIn', icon: Linkedin, href: 'https://linkedin.com/in/aayush-panwar' },
              { label: 'Website', icon: Globe, href: 'https://portfolio.aayushp.dev' }
            ].map((item, idx) => (
              <a
                key={idx}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <item.icon size={16} className="opacity-70" />
                <span className="text-xs font-medium">{item.label}</span>
              </a>
            ))}
          </div>
        </div>
      );
    }

    // Default: Text Message (with Markdown)
    return (
      <div className={`px-4 py-2.5 max-w-[85%] md:max-w-[75%] break-words shadow-sm ${msg.sender === 'you'
        ? theme.chat.userBubble
        : theme.chat.botBubble + ' bubble-enter'
        } ${msg.streaming ? 'streaming-bubble' : ''}`}>
        {msg.sender === 'aayush' && msg.streaming ? (
          // While streaming, render plain words so each can fade in on its own;
          // we swap to full Markdown once the reply is finalized.
          <p className="mb-2 last:mb-0 leading-relaxed">
            {msg.text.split(/(\s+)/).map((chunk, i) =>
              /^\s+$/.test(chunk)
                ? chunk
                : <span key={i} className="flow-word">{chunk}</span>
            )}
          </p>
        ) : (
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            p: ({ node, ...props }) => <p className="mb-2 last:mb-0 leading-relaxed" {...props} />,
            strong: ({ node, ...props }) => <span className="font-bold" {...props} />,
            em: ({ node, ...props }) => <span className="italic" {...props} />,
            ul: ({ node, ...props }) => <ul className="list-disc pl-4 my-2 space-y-1" {...props} />,
            ol: ({ node, ...props }) => <ol className="list-decimal pl-4 my-2 space-y-1" {...props} />,
            li: ({ node, ...props }) => <li className="" {...props} />,
            a: ({ node, ...props }) => <a className="underline opacity-80 hover:opacity-100" target="_blank" rel="noopener noreferrer" {...props} />,
            code: ({ node, inline, ...props }) => inline
              ? <code className="bg-black/10 px-1 py-0.5 rounded text-xs font-mono" {...props} />
              : <code className="block bg-black/10 p-2 rounded text-xs font-mono my-2 overflow-x-auto whitespace-pre-wrap" {...props} />,
            blockquote: ({ node, ...props }) => <blockquote className="border-l-2 border-current pl-3 opacity-80 my-2 italic" {...props} />
          }}
        >
          {msg.text}
        </ReactMarkdown>
        )}
      </div>
    );
  };

  return (
    <div className={`transition-all duration-500 ease-in-out ${theme.wrapper} 
      fixed inset-0 overflow-hidden p-0                            /* Mobile: Fixed to viewport, no scroll */
      md:relative md:inset-auto md:min-h-screen md:flex md:items-center md:justify-center md:p-6 /* Desktop: Normal flow */
    `}>

      <ResumeDownloadModal
        isOpen={showResumeModal}
        onClose={() => setShowResumeModal(false)}
        theme={theme}
      />

      {/* Main Chat Container */}
      <div className={`flex flex-col relative overflow-hidden transition-all duration-500 ease-in-out ${theme.container}
        w-full h-full rounded-none                               /* Mobile: Full size, square corners */
        md:w-full md:max-w-6xl md:h-[90vh] md:rounded-3xl md:shadow-2xl /* Desktop: Wide, tall, rounded, shadowed */
      `}>

        {/* Header */}
        <div className="p-4 md:p-6 flex items-center justify-between border-b border-black/5 shrink-0">
          <div className="flex items-center gap-3 md:gap-5">
            <div
              ref={orbRef}
              className={`ai-orb ai-orb--theme-${currentTheme} ai-orb--${logoState} w-10 h-10 md:w-12 md:h-12`}
              role="img"
              aria-label={`Aayush AI is ${logoState}`}
            >
              <span className="ai-orb__glow" aria-hidden="true" />
              <span className="ai-orb__core" aria-hidden="true" />
            </div>
            <div>
              <h1 className={`text-lg md:text-xl font-bold transition-all duration-500 ${theme.header.text}`}>hey, i'm aayush</h1>
            </div>
          </div>

          <div className="flex items-center gap-1 md:gap-3">
            {/* Theme Switcher - Distinctive Button */}
            <button
              onClick={cycleTheme}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-300 mr-2
                ${currentTheme === 'original' ? 'bg-gray-100 hover:bg-gray-200 text-black border border-black/5' : ''}
                ${currentTheme === 'clay' ? 'bg-white/50 hover:bg-white/80 text-indigo-900 border border-white/60' : ''}
              `}
              title="Switch Theme"
            >
              <span className="text-xs font-medium hidden md:block">Theme</span>
              <Palette size={16} className="md:w-4 md:h-4" />
            </button>

            {[
              { Icon: Globe, href: "https://portfolio.aayushp.dev", label: "Portfolio" },
              { Icon: Github, href: "https://github.com/aayush-panwar", label: "GitHub" },
              { Icon: Linkedin, href: "https://linkedin.com/in/aayush-panwar", label: "LinkedIn" }
            ].map(({ Icon, href, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                title={label}
                aria-label={label}
                className={`p-2 transition-all duration-300 ${theme.header.icon}`}
              >
                <Icon size={18} className="md:w-5 md:h-5" />
              </a>
            ))}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 scroll-smooth" id="chat-container">
          {/* Initial Question / Onboarding */}
          {showInitialQuestion && (
            <div className={`max-w-2xl mx-auto mt-10 md:mt-20 transition-all duration-700 ${showInitialQuestion ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <h2 className={`text-2xl md:text-4xl font-semibold mb-3 text-center tracking-tight ${theme.text.primary}`}>
                what brings you here?
              </h2>
              <p className="text-center text-sm md:text-base opacity-60 mb-8 max-w-md mx-auto">
                Ask me anything about Aayush — his work, his story, or how to work together. Pick a lane to start:
              </p>
              <div className="flex flex-wrap justify-center gap-2.5 md:gap-3">
                {[
                  { label: 'build with me', Icon: Briefcase },
                  { label: 'date me maybe', Icon: Heart },
                  { label: 'let\u2019s talk', Icon: MessageCircle },
                  { label: 'back my ideas', Icon: Rocket },
                ].map(({ label, Icon }) => (
                  <button
                    key={label}
                    onClick={() => handleOptionClick(label)}
                    className={`group inline-flex items-center gap-2 px-5 py-2.5 rounded-full border text-sm md:text-base transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${theme.button.option}`}
                  >
                    <Icon size={15} className="opacity-60 group-hover:opacity-100 transition-opacity" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex flex-col ${msg.sender === 'you' ? 'items-end' : 'items-start'} animate-in fade-in slide-in-from-bottom-2 duration-300`}
            >
              <span className="text-[10px] opacity-50 mb-1 px-1">
                {msg.sender === 'you' ? 'you' : 'aayush'}
              </span>
              {renderMessage(msg)}
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex flex-col items-start animate-in fade-in duration-300">
              <span className="text-[10px] opacity-50 mb-1 px-1">aayush</span>
              <div className={`px-4 py-3 transition-all duration-300 ${theme.chat.botBubble}`}>
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce"></span>
                  <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce [animation-delay:0.2s]"></span>
                  <span className="w-1.5 h-1.5 bg-current rounded-full animate-bounce [animation-delay:0.4s]"></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Chat Input Area */}
        <div className={`shrink-0 border-t border-black/5 ${theme.input.container} sticky bottom-0 z-20
          p-3 pb-[max(12px,env(safe-area-inset-bottom))] /* Mobile: Compact padding + safe area */
          md:p-5                                          /* Desktop: Spacious padding */
        `}>
          {/* Modern AI prompt box: auto-growing textarea + inline send button */}
          <div className={`flex items-end gap-2 rounded-[24px] border border-current/10 transition-all duration-300
            px-2 py-1.5 md:px-2.5 md:py-2
            focus-within:border-current/25 focus-within:shadow-lg focus-within:-translate-y-[1px]`}>
            <textarea
              ref={inputRef}
              rows={1}
              maxLength={3000}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              placeholder={isTyping ? "Aayush is typing…" : "Ask me anything…"}
              className="flex-1 resize-none bg-transparent outline-none border-none max-h-40 overflow-y-auto
                px-3 py-2.5 text-sm md:text-base leading-relaxed placeholder:opacity-40"
            />
            <button
              onClick={handleSendMessage}
              disabled={!inputValue.trim() || isLoading}
              aria-label="Send message"
              className={`group shrink-0 grid place-items-center transition-all duration-300
                w-9 h-9 md:w-10 md:h-10 disabled:opacity-40 disabled:cursor-not-allowed
                ${theme.input.sendBtn}`}
            >
              <Send size={17} className="transition-transform group-hover:translate-x-[1px] group-active:scale-90 md:w-[18px] md:h-[18px]" />
            </button>
          </div>

          {/* Helper row */}
          <div className="flex items-center justify-center gap-2 mt-2 text-[10px] md:text-xs opacity-50">
            <span className="hidden md:inline">Enter to send · Shift + Enter for a new line</span>
            <span className="hidden md:inline opacity-40">·</span>
            <Link to="/about" className="hover:opacity-100 transition-opacity underline-offset-2 hover:underline">
              Prefer textual version?
            </Link>
          </div>
        </div>

      </div>
      {/* About Page Link (Hidden but accessible via route) */}
      <Link to="/about" className="fixed top-4 right-4 opacity-0 hover:opacity-100 transition-opacity">
        About
      </Link>
    </div>
  );
};

export default ChatInterface;
